import { useCallback, useContext } from "react";
import { ReactReduxContext } from "react-redux";
import {
  type EventTransformerHooks,
  type GlobalTrackingAttributes,
  useEventTracking,
  type UseInstantTransformEvent,
} from "@b2bportal/core-tracking";
import type { Reservation } from "@b2bportal/lodging-api";
import type { TripsListState } from "../features/list/store";
import {
  type CompleteCancelFailureHotel,
  type CompleteCancelSuccessHotel,
  type TappedCallContactSupportHotel,
  type TappedCancelGoBackHotel,
  type TappedCancelHotel,
  type TappedConfirmCancelHotel,
  type TappedContactSupportHotel,
  type TripsHTSEvent,
  TripsTrackingEvents,
  type ViewCancelModalHotel,
  type ViewedMyTrips,
  type ViewedTripSummaryHotel,
} from "./events";
import { getHotelBookingTrackingProperties } from "./selectors";
import {
  type CompleteCancelFailureHotelRawEvent,
  type CompleteCancelSuccessHotelRawEvent,
  type HotelBookingTrackingProperties,
  type TappedCallContactSupportHotelRawEvent,
  type TappedCancelGoBackHotelRawEvent,
  type TappedCancelHotelRawEvent,
  type TappedConfirmCancelHotelRawEvent,
  type TappedContactSupportHotelRawEvent,
  type TripsRawEvent,
  TripsRawEventName,
  type ViewCancelModalHotelRawEvent,
  type ViewedMyTripsRawEvent,
  type ViewedTripSummaryHotelRawEvent,
} from "./types";

export const defaultHotelBookingEventTransformer = <T extends TripsHTSEvent>(
  eventName: T["name"],
  trackingAttributes: GlobalTrackingAttributes,
  extraProperties: Omit<
    T["properties"],
    keyof HotelBookingTrackingProperties["properties"]
  >
): {
  name: T["name"];
  properties: T["properties"];
} => {
  const { properties } = getHotelBookingTrackingProperties(
    trackingAttributes["hotelReservation"] as Reservation
  );
  return {
    name: eventName,
    properties: {
      ...properties,
      ...extraProperties,
    },
  };
};

export const useTripsViewedMyTripsTransformer: UseInstantTransformEvent<
  ViewedMyTripsRawEvent,
  ViewedMyTrips
> = () => {
  return useCallback((event) => {
    const name: TripsTrackingEvents.viewed_my_trips =
      TripsTrackingEvents.viewed_my_trips;
    const htsEvent = {
      name,
      properties: {},
    };

    return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
  }, []);
};

export const useTripsViewedTripSummaryTransformer: UseInstantTransformEvent<
  ViewedTripSummaryHotelRawEvent,
  ViewedTripSummaryHotel
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(
    (event) => {
      const htsEvent =
        defaultHotelBookingEventTransformer<ViewedTripSummaryHotel>(
          TripsTrackingEvents.viewed_trip_summary,
          getAllAttributes(),
          {}
        );

      return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
    },
    [getAllAttributes]
  );
};

export const useTripsTappedCancelHotelTransformer: UseInstantTransformEvent<
  TappedCancelHotelRawEvent,
  TappedCancelHotel
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(
    (event) => {
      const htsEvent = defaultHotelBookingEventTransformer<TappedCancelHotel>(
        TripsTrackingEvents.tapped_cancel_hotel,
        getAllAttributes(),
        {}
      );

      return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
    },
    [getAllAttributes]
  );
};

export const useTripsViewCancelModalHotelTransformer: UseInstantTransformEvent<
  ViewCancelModalHotelRawEvent,
  ViewCancelModalHotel
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(
    (event) => {
      const htsEvent =
        defaultHotelBookingEventTransformer<ViewCancelModalHotel>(
          TripsTrackingEvents.view_cancel_modal,
          getAllAttributes(),
          {}
        );

      return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
    },
    [getAllAttributes]
  );
};

export const useTripsTappedConfirmCancelHotelTransformer: UseInstantTransformEvent<
  TappedConfirmCancelHotelRawEvent,
  TappedConfirmCancelHotel
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(
    (event) => {
      const htsEvent =
        defaultHotelBookingEventTransformer<TappedConfirmCancelHotel>(
          TripsTrackingEvents.tapped_confirm_cancel,
          getAllAttributes(),
          {}
        );

      return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
    },
    [getAllAttributes]
  );
};

export const useTripsTappedCancelGoBackHotelTransformer: UseInstantTransformEvent<
  TappedCancelGoBackHotelRawEvent,
  TappedCancelGoBackHotel
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(
    (event) => {
      const htsEvent =
        defaultHotelBookingEventTransformer<TappedCancelGoBackHotel>(
          TripsTrackingEvents.tapped_cancel_go_back,
          getAllAttributes(),
          {}
        );

      return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
    },
    [getAllAttributes]
  );
};

export const useTripsTappedContactSupportHotelTransformer: UseInstantTransformEvent<
  TappedContactSupportHotelRawEvent,
  TappedContactSupportHotel
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(
    (event) => {
      const htsEvent =
        defaultHotelBookingEventTransformer<TappedContactSupportHotel>(
          TripsTrackingEvents.tapped_contact_support,
          getAllAttributes(),
          {}
        );

      return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
    },
    [getAllAttributes]
  );
};

export const useTripsTappedCallContactSupportHotelTransformer: UseInstantTransformEvent<
  TappedCallContactSupportHotelRawEvent,
  TappedCallContactSupportHotel
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(
    (event) => {
      const htsEvent =
        defaultHotelBookingEventTransformer<TappedCallContactSupportHotel>(
          TripsTrackingEvents.tapped_call_contact_support,
          getAllAttributes(),
          {}
        );

      return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
    },
    [getAllAttributes]
  );
};

export const useTripsCompleteCancelSuccessHotelTransformer: UseInstantTransformEvent<
  CompleteCancelSuccessHotelRawEvent,
  CompleteCancelSuccessHotel
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(
    (event) => {
      const htsEvent =
        defaultHotelBookingEventTransformer<CompleteCancelSuccessHotel>(
          TripsTrackingEvents.complete_cancel_success,
          getAllAttributes(),
          {}
        );

      return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
    },
    [getAllAttributes]
  );
};

export const useTripsCompleteCancelFailureHotelTransformer: UseInstantTransformEvent<
  CompleteCancelFailureHotelRawEvent,
  CompleteCancelFailureHotel
> = () => {
  const { getAllAttributes } = useEventTracking();

  return useCallback(
    (event) => {
      const htsEvent =
        defaultHotelBookingEventTransformer<CompleteCancelFailureHotel>(
          TripsTrackingEvents.complete_cancel_failure,
          getAllAttributes(),
          {}
        );

      return shouldTrackEvent(htsEvent) ? htsEvent : undefined;
    },
    [getAllAttributes]
  );
};

export const TRIPS_HTS_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  TripsRawEvent,
  TripsHTSEvent
> = {
  [TripsRawEventName.ViewedMyTrips]: [useTripsViewedMyTripsTransformer],
  [TripsRawEventName.ViewedTripSummaryHotel]: [
    useTripsViewedTripSummaryTransformer,
  ],
  [TripsRawEventName.TappedCancelHotel]: [useTripsTappedCancelHotelTransformer],
  [TripsRawEventName.ViewCancelModalHotel]: [
    useTripsViewCancelModalHotelTransformer,
  ],
  [TripsRawEventName.TappedConfirmCancelHotel]: [
    useTripsTappedConfirmCancelHotelTransformer,
  ],
  [TripsRawEventName.TappedCancelGoBackHotel]: [
    useTripsTappedCancelGoBackHotelTransformer,
  ],
  [TripsRawEventName.TappedContactSupportHotel]: [
    useTripsTappedContactSupportHotelTransformer,
  ],
  [TripsRawEventName.TappedCallContactSupportHotel]: [
    useTripsTappedCallContactSupportHotelTransformer,
  ],
  [TripsRawEventName.CompleteCancelSuccessHotel]: [
    useTripsCompleteCancelSuccessHotelTransformer,
  ],
  [TripsRawEventName.CompleteCancelFailureHotel]: [
    useTripsCompleteCancelFailureHotelTransformer,
  ],
};

export const shouldTrackEvent = (event: TripsHTSEvent): boolean => {
  return true;
};

function useGetProviders(): () => Providers {
  const { getAllAttributes } = useEventTracking();
  const reduxContext = useContext(ReactReduxContext);

  return useCallback(
    () => ({
      trackingAttributes: getAllAttributes(),
      reduxState: reduxContext.store.getState(),
    }),
    [reduxContext.store, getAllAttributes]
  );
}

export interface Providers {
  trackingAttributes: GlobalTrackingAttributes;
  reduxState: TripsListState;
}
